import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, getAuthHeaders } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/ws/messages`.replace(/^http/, "ws");
const UnreadMessagesContext = createContext({ unreadCount: 0, refetchUnread: () => {}, soundEnabled: true, setSoundEnabled: () => {}, subscribeToMessages: () => () => {}, sendTyping: () => {}, setActiveConversation: () => {} });
const SOUND_PREF_KEY = "infocity_message_sound_enabled";

// Single shared realtime channel for Mensajes — one WebSocket connection per
// logged-in session, reused by BOTH roles (no candidate/employer-specific
// logic here). MessagesPage subscribes to the same event stream instead of
// opening its own socket — "una única lógica compartida" as requested.
// Polling stays as a fallback only (badge self-heals every 30s if the socket
// is ever down), never the primary mechanism.
export function UnreadMessagesProvider({ children }) {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabledState] = useState(() => localStorage.getItem(SOUND_PREF_KEY) !== "false");
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  // Presence: which conversation (if any) is actually open+visible right now
  // in THIS tab. Sent to the backend on every change/reconnect so it can
  // decide whether a push notification would be redundant — never inferred
  // just from "the socket is connected".
  const activeConversationIdRef = useRef(null);
  const tabVisibleRef = useRef(typeof document === "undefined" || document.visibilityState !== "hidden");

  const setSoundEnabled = useCallback((v) => {
    setSoundEnabledState(v);
    localStorage.setItem(SOUND_PREF_KEY, v ? "true" : "false");
  }, []);

  const playSound = useCallback(() => {
    if (!soundEnabled) return;
    const audio = new Audio("/sounds/message-notification.wav");
    audio.volume = 0.5;
    audio.play().catch(() => {});
  }, [soundEnabled]);

  const sendPresence = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "presence", conversation_id: activeConversationIdRef.current, visible: tabVisibleRef.current }));
    }
  }, []);

  const setActiveConversation = useCallback((conversationId) => {
    activeConversationIdRef.current = conversationId;
    sendPresence();
  }, [sendPresence]);

  // Foreground/background flips must update presence immediately (locking the
  // phone or switching tabs must not leave the backend thinking the chat is
  // still being watched).
  useEffect(() => {
    const onVisibility = () => {
      tabVisibleRef.current = document.visibilityState === "visible";
      sendPresence();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [sendPresence]);

  const refetchUnread = useCallback(async () => {
    if (!user) { setUnreadCount(0); return; }
    try {
      const res = await axios.get(`${API}/conversations`, { headers: getAuthHeaders() });
      const total = (res.data?.conversations || []).reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setUnreadCount(total);
    } catch { /* keep last known value on a transient failure */ }
  }, [user]);

  useEffect(() => {
    refetchUnread();
    if (!user) return;
    const interval = setInterval(refetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user, refetchUnread]);

  // App-open feedback for a push received while the tab is focused — the SW
  // skips showing an OS notification in that case and posts this message
  // instead (kept as a supplement to the WS connection below, harmless if
  // both fire since refetchUnread is idempotent).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event) => {
      if (event.data?.type !== "NEW_MESSAGE_PUSH") return;
      refetchUnread();
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [refetchUnread]);

  // ── WebSocket connection lifecycle ──
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!user || !token) {
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      return;
    }

    let closedByUs = false;

    const connect = () => {
      const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        sendPresence();
      };

      ws.onmessage = (event) => {
        let data;
        try { data = JSON.parse(event.data); } catch { return; }
        if (data.type === "new_message") {
          if (typeof data.total_unread === "number") setUnreadCount(data.total_unread);
          const viewingThisConvo = data.conversation_id === activeConversationIdRef.current && tabVisibleRef.current;
          if (data.message?.sender_id !== user.user_id && !viewingThisConvo) playSound();
        }
        listenersRef.current.forEach((cb) => { try { cb(data); } catch { /* one bad listener shouldn't break the rest */ } });
      };

      ws.onclose = () => {
        if (closedByUs) return;
        // Reconnect with capped backoff — never leaves an orphaned/dead
        // connection lingering, never spins in a tight retry loop either.
        reconnectAttemptRef.current += 1;
        const delay = Math.min(1000 * 2 ** reconnectAttemptRef.current, 15000);
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => { ws.close(); };
    };

    connect();
    return () => {
      closedByUs = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
      wsRef.current = null;
    };
  }, [user, playSound, sendPresence]);

  const subscribeToMessages = useCallback((callback) => {
    listenersRef.current.add(callback);
    return () => listenersRef.current.delete(callback);
  }, []);

  const sendTyping = useCallback((conversationId, isTyping) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "typing", conversation_id: conversationId, is_typing: isTyping }));
    }
  }, []);

  return (
    <UnreadMessagesContext.Provider value={{ unreadCount, refetchUnread, soundEnabled, setSoundEnabled, subscribeToMessages, sendTyping, setActiveConversation }}>
      {children}
    </UnreadMessagesContext.Provider>
  );
}

export function useUnreadMessages() {
  return useContext(UnreadMessagesContext);
}
